import React, { FC, useMemo } from 'react';
import { ProductFilter, ProductMetaData } from '../../../../models/Product';
import { LoanFormat, mapLoanFormat } from '../../../../models/LoanFormat';
import { FilterSetting, ProductSpec } from '../../../store/api';
import Utils from '../../../shared/utils';
import RoutingUtils from '../../../shared/utils/utils-routing';
import { LibxCatalogueProductGroup } from '../../../shared/product/ProductGroup';

type Props = {
    readonly product: ProductMetaData;
};

export const LibxProductDetailProductGroups: FC<Props> = ({ product }) => {
    return (
        <>
            <SeriesProductGroup loanFormat={LoanFormat.eBooks} product={product} />
            <AuthorProductGroup loanFormat={LoanFormat.eBooks} product={product} />
            <GenreProductGroup loanFormat={LoanFormat.eBooks} product={product} />
        </>
    );
};

type SeriesProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
};
const SeriesProductGroup: FC<SeriesProductGroupProps> = ({ loanFormat, product, language }) => {
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                series: { include: [product.seriesName!] },
                ...languageFilter
            }
        }),
        [product, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl(product, loanFormat, productSpec.filters!);

    const titlePrefix = loanFormat === mapLoanFormat(product.loanFormat) ? 'More' : '';
    const title = language
        ? `${titlePrefix} ${loanFormat} in ${language} from ${product.seriesName}`
        : `${titlePrefix} ${loanFormat} from ${product.seriesName}`;

    if (!product.seriesName) {
        return null;
    }

    return <LibxCatalogueProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
};

type AuthorProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
};
const AuthorProductGroup: FC<AuthorProductGroupProps> = ({ loanFormat, product, language }) => {
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);

    const authors = useMemo(() => (product.authors ? product.authors.filter(a => !!a.id) : []), [product]);

    const authorNames = authors.map(a => Utils.getPerformerFullname(a)).join(', ');
    const authorIds = useMemo(() => authors.map(a => a.id!) ?? [], [authors]);

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                author: { include: authorIds },
                ...languageFilter
            }
        }),
        [authorIds, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl(product, loanFormat, productSpec.filters!);

    const titlePrefix = loanFormat === mapLoanFormat(product.loanFormat) ? 'More' : '';
    const title = language
        ? `${titlePrefix} ${loanFormat} in ${language} from ${authorNames}`
        : `${titlePrefix} ${loanFormat} from ${authorNames}`;

    if (!product.authors || product.authors.length === 0) {
        return null;
    }

    return <LibxCatalogueProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
};

type GenreProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
};
const GenreProductGroup: FC<GenreProductGroupProps> = ({ loanFormat, product, language }) => {
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);

    const genreNames = product.genres
        ? product.genres.map(g => g.name + (g.subGenres ? ` > ${g.subGenres[0].name}` : '')).join(', ')
        : '';
    const genreIds = useMemo(
        () => (product.genres ? product.genres.map(g => (g.subGenres ? g.subGenres[0].id : g.id)) : []),
        [product.genres]
    );

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                genre: { include: genreIds },
                ...languageFilter
            }
        }),
        [languageFilter, genreIds, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl(product, loanFormat, productSpec.filters!);

    const titlePrefix = loanFormat === mapLoanFormat(product.loanFormat) ? 'More' : '';
    const title = language
        ? `${titlePrefix} ${loanFormat} in ${language} from ${genreNames}`
        : `${titlePrefix} ${loanFormat} from ${genreNames}`;

    if (!product.genres || product.genres.length === 0) {
        return null;
    }
    return <LibxCatalogueProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
};

const buildSeeAllUrl = (
    product: ProductMetaData,
    loanFormat: LoanFormat,
    filter: { [key: string]: FilterSetting },
    targetLanguage?: string
): string => {
    switch (product.productClass) {
        case 'STANDARD':
            return Utils.catalogueLocationDescriptorString(loanFormat, filter);
        case 'LOTE':
            return RoutingUtils.foreignLanguageDescriptorString(targetLanguage, loanFormat, filter);
        case 'LANGUAGE_LEARNING':
            return RoutingUtils.languageLearningDescriptorString(targetLanguage, loanFormat, filter);
        case 'CAMPAIGN':
            return Utils.catalogueLocationDescriptorString(loanFormat, filter);
        case 'LOCAL':
            return '';
    }
};

const buildLanguageFilter = (product: ProductMetaData, language?: string): ProductFilter | undefined => {
    if (!language) return undefined;
    if (!product.language) return undefined;

    if (product.language === language) {
        return { language: { include: [product.language] } };
    }

    return { language: { exclude: [product.language] } };
};
